import React, { useState } from 'react';

// Define types for our component
interface SqlToPhpArrayProps { }

// Define database types
type DatabaseTable = {
    name: string;
    records: Record<string, any>[];
};

// Sample database tables for demo purposes
const mockDatabase: Record<string, DatabaseTable> = {
    users: {
        name: 'users',
        records: [
            { id: 1, name: 'John Doe', email: 'john@example.com', created_at: '2023-01-15' },
            { id: 2, name: 'Jane Smith', email: 'jane@example.com', created_at: '2023-02-20' },
            { id: 3, name: 'Bob Johnson', email: 'bob@example.com', created_at: '2023-03-10' },
            { id: 4, name: 'Alice Brown', email: 'alice@example.com', created_at: '2023-04-05' },
            { id: 5, name: 'Charlie Davis', email: 'charlie@example.com', created_at: '2023-05-12' }
        ]
    },
    products: {
        name: 'products',
        records: [
            { id: 101, name: 'Laptop', price: 1299.99, category: 'Electronics', in_stock: true },
            { id: 102, name: 'Smartphone', price: 899.99, category: 'Electronics', in_stock: true },
            { id: 103, name: 'Headphones', price: 199.99, category: 'Electronics', in_stock: false },
            { id: 104, name: 'Desk Chair', price: 249.99, category: 'Furniture', in_stock: true },
            { id: 105, name: 'Coffee Table', price: 349.99, category: 'Furniture', in_stock: true },
            { id: 106, name: 'Bookshelf', price: 199.99, category: 'Furniture', in_stock: false },
            { id: 107, name: 'Water Bottle', price: 24.99, category: 'Kitchen', in_stock: true },
            { id: 108, name: 'Blender', price: 89.99, category: 'Kitchen', in_stock: true }
        ]
    },
    orders: {
        name: 'orders',
        records: [
            { id: 1001, user_id: 1, total: 1299.99, status: 'Delivered', order_date: '2023-06-15' },
            { id: 1002, user_id: 2, total: 899.99, status: 'Shipped', order_date: '2023-06-20' },
            { id: 1003, user_id: 3, total: 249.99, status: 'Processing', order_date: '2023-06-25' },
            { id: 1004, user_id: 1, total: 89.99, status: 'Delivered', order_date: '2023-07-01' },
            { id: 1005, user_id: 4, total: 549.98, status: 'Shipped', order_date: '2023-07-05' },
            { id: 1006, user_id: 5, total: 199.99, status: 'Processing', order_date: '2023-07-10' }
        ]
    }
};

// SQL to PHP Array Converter
const sqlToPhpArray = (sqlQuery: string, database: Record<string, DatabaseTable>) => {
    sqlQuery = sqlQuery.trim();

    // Basic SELECT statement parsing
    if (sqlQuery.toLowerCase().startsWith('select')) {
        // Parse SQL query components
        const selectMatch = sqlQuery.match(/select\s+(.*?)\s+from\s+(\w+)(?:\s+where\s+(.*?))?(?:\s+order\s+by\s+(.*?))?(?:\s+limit\s+(\d+))?;?\s*$/i);

        if (!selectMatch) {
            throw new Error('Invalid SQL query format. Expected: SELECT columns FROM table [WHERE condition] [ORDER BY columns] [LIMIT number]');
        }

        const columns = selectMatch[1].trim();
        const tableName = selectMatch[2].trim();
        const whereClause = selectMatch[3] ? selectMatch[3].trim() : null;
        const orderByClause = selectMatch[4] ? selectMatch[4].trim() : null;
        const limitClause = selectMatch[5] ? parseInt(selectMatch[5].trim()) : null;

        // Check if table exists
        if (!database[tableName]) {
            throw new Error(`Table '${tableName}' does not exist`);
        }

        const table = database[tableName];

        // Generate column selection
        let selectedColumns: string[] = [];
        if (columns === '*') {
            // If selecting all columns, get all column names from the first record
            if (table.records.length > 0) {
                selectedColumns = Object.keys(table.records[0]);
            }
        } else {
            // For specific columns
            selectedColumns = columns.split(',').map(col => col.trim());
        }

        // Generate WHERE clause
        let filter: ((record: Record<string, any>) => boolean) | null = null;

        if (whereClause) {
            // Parse the WHERE clause
            const whereMatch = whereClause.match(/(\w+)\s*(=|!=|>|<|>=|<=|LIKE)\s*([^;]*)/i);

            if (whereMatch) {
                const field = whereMatch[1].trim();
                const operator = whereMatch[2].trim().toUpperCase();
                let value: string | number | boolean = whereMatch[3].trim();

                // Remove quotes from string values
                if ((value.startsWith("'") && value.endsWith("'")) || 
                    (value.startsWith('"') && value.endsWith('"'))) {
                    value = value.substring(1, value.length - 1);
                }
                
                // Convert to appropriate type if needed
                let typedValue: string | number | boolean = value;
                if (value === 'true') typedValue = true;
                else if (value === 'false') typedValue = false;
                else if (!isNaN(Number(value)) && value !== '') typedValue = Number(value);

                // Create filter function based on operator
                filter = (record: Record<string, any>) => {
                    const recordValue = record[field];
                    
                    switch (operator) {
                        case '=':
                            return recordValue === typedValue;
                        case '!=':
                            return recordValue !== typedValue;
                        case '>':
                            return recordValue > typedValue;
                        case '<':
                            return recordValue < typedValue;
                        case '>=':
                            return recordValue >= typedValue;
                        case '<=':
                            return recordValue <= typedValue;
                        case 'LIKE':
                            // Simple LIKE implementation
                            if (typeof recordValue === 'string' && typeof typedValue === 'string') {
                                // Replace SQL wildcards with regex equivalents
                                const pattern = typedValue.replace(/%/g, '.*').replace(/_/g, '.');
                                const regex = new RegExp(`^${pattern}$`, 'i');
                                return regex.test(recordValue);
                            }
                            return false;
                        default:
                            return false;
                    }
                };
            } else {
                throw new Error('Invalid WHERE clause format. Only basic comparison operators are supported.');
            }
        }

        // Generate ORDER BY
        let sorter: ((a: Record<string, any>, b: Record<string, any>) => number) | null = null;

        if (orderByClause) {
            const orderParts = orderByClause.split(' ');
            const orderField = orderParts[0].trim();
            const isDesc = orderParts.length > 1 && orderParts[1].trim().toLowerCase() === 'desc';
            
            sorter = (a: Record<string, any>, b: Record<string, any>) => {
                if (a[orderField] < b[orderField]) return isDesc ? 1 : -1;
                if (a[orderField] > b[orderField]) return isDesc ? -1 : 1;
                return 0;
            };
        }

        // Execute query
        let results = [...table.records];

        // Apply filter (WHERE)
        if (filter) {
            results = results.filter(filter);
        }

        // Apply sort (ORDER BY)
        if (sorter) {
            results.sort(sorter);
        }

        // Apply limit
        if (limitClause !== null && !isNaN(limitClause)) {
            results = results.slice(0, limitClause);
        }

        // Apply projection (SELECT columns)
        if (selectedColumns.length > 0) {
            results = results.map(record => {
                const projectedRecord: Record<string, any> = {};
                selectedColumns.forEach(column => {
                    if (column in record) {
                        projectedRecord[column] = record[column];
                    }
                });
                return projectedRecord;
            });
        }

        // Generate PHP query object
        const phpQuery = {
            table: tableName,
            select: selectedColumns,
            where: whereClause,
            orderBy: orderByClause,
            limit: limitClause
        };

        // Return the PHP query and the results
        return {
            phpQuery,
            results
        };
    } else {
        throw new Error('Only SELECT queries are supported for conversion to PHP arrays');
    }
};

// Format PHP array syntax
const formatPhpArraySyntax = (results: any[], tableName: string): string => {
    // Start PHP array
    let php = "<?php\n\n";
    php += `// Data for ${tableName} table\n`;
    php += `$${tableName} = [\n`;
    
    // Add each record as an array
    results.forEach((record, index) => {
        php += `    [\n`;
        
        // Add record fields
        Object.entries(record).forEach(([key, value], i, arr) => {
            const formattedValue = formatPhpValue(value);
            php += `        '${key}' => ${formattedValue}${i < arr.length - 1 ? ',' : ''}\n`;
        });
        
        php += `    ]${index < results.length - 1 ? ',' : ''}\n`;
    });
    
    php += "];\n";
    return php;
};

// Format PHP PDO query
const formatPhpPdoQuery = (phpQuery: any): string => {
    const { table, select, where, orderBy, limit } = phpQuery;
    
    let php = "<?php\n\n";
    php += "// Database connection\n";
    php += "$host = 'localhost';\n";
    php += "$dbname = 'database';\n";
    php += "$username = 'username';\n";
    php += "$password = 'password';\n\n";
    
    php += "try {\n";
    php += "    $pdo = new PDO(\"mysql:host=$host;dbname=$dbname\", $username, $password);\n";
    php += "    $pdo->setAttribute(PDO::ATTR_ERRMODE, PDO::ERRMODE_EXCEPTION);\n\n";
    
    // Build the SQL query
    php += "    // Build query\n";
    php += `    $sql = \"SELECT ${select.join(', ')} FROM ${table}`;
    
    // Add WHERE clause if exists
    if (where) {
        php += ` WHERE ${where}`;
    }
    
    // Add ORDER BY clause if exists
    if (orderBy) {
        php += ` ORDER BY ${orderBy}`;
    }
    
    // Add LIMIT clause if exists
    if (limit !== null) {
        php += ` LIMIT ${limit}`;
    }
    
    php += "\";\n\n";
    
    // Prepare and execute query
    php += "    // Prepare and execute the query\n";
    php += "    $stmt = $pdo->prepare($sql);\n";
    php += "    $stmt->execute();\n\n";
    
    // Fetch results
    php += "    // Fetch results\n";
    php += "    $results = $stmt->fetchAll(PDO::FETCH_ASSOC);\n\n";
    
    // Output results
    php += "    // Process the results\n";
    php += "    foreach ($results as $row) {\n";
    php += "        // Process each row\n";
    php += "        print_r($row);\n";
    php += "    }\n";
    
    php += "} catch (PDOException $e) {\n";
    php += "    echo \"Error: \" . $e->getMessage();\n";
    php += "}\n";
    
    return php;
};

// Format PHP Laravel query
const formatPhpLaravelQuery = (phpQuery: any): string => {
    const { table, select, where, orderBy, limit } = phpQuery;
    
    let php = "<?php\n\n";
    php += "namespace App\\Http\\Controllers;\n\n";
    php += "use Illuminate\\Http\\Request;\n";
    php += "use Illuminate\\Support\\Facades\\DB;\n\n";
    
    php += "class QueryController extends Controller\n{\n";
    php += "    public function executeQuery()\n    {\n";
    
    // Start the query
    php += "        // Laravel DB Query Builder\n";
    php += `        $results = DB::table('${table}')\n`;
    
    // Add SELECT clause
    php += `            ->select('${select.join("', '")}')\n`;
    
    // Add WHERE clause if exists
    if (where) {
        const whereMatch = where.match(/(\w+)\s*(=|!=|>|<|>=|<=|LIKE)\s*([^;]*)/i);
        if (whereMatch) {
            const field = whereMatch[1].trim();
            const operator = whereMatch[2].trim();
            let value = whereMatch[3].trim();
            
            // Remove quotes from string values
            if ((value.startsWith("'") && value.endsWith("'")) || 
                (value.startsWith('"') && value.endsWith('"'))) {
                value = value.substring(1, value.length - 1);
            }
            
            php += `            ->where('${field}', '${operator}', ${formatPhpValue(value)})\n`;
        }
    }
    
    // Add ORDER BY clause if exists
    if (orderBy) {
        const orderParts = orderBy.split(' ');
        const orderField = orderParts[0].trim();
        const direction = orderParts.length > 1 && orderParts[1].trim().toLowerCase() === 'desc' ? 'desc' : 'asc';
        
        php += `            ->orderBy('${orderField}', '${direction}')\n`;
    }
    
    // Add LIMIT clause if exists
    if (limit !== null) {
        php += `            ->limit(${limit})\n`;
    }
    
    // Get the results
    php += "            ->get();\n\n";
    
    // Return the results
    php += "        return response()->json($results);\n";
    php += "    }\n";
    php += "}\n";
    
    return php;
};

// Helper function to format PHP values correctly
const formatPhpValue = (value: any): string => {
    if (value === null) {
        return 'null';
    }
    
    if (typeof value === 'string') {
        // Escape quotes in string
        const escaped = value.replace(/'/g, "\\'");
        return `'${escaped}'`;
    }
    
    if (typeof value === 'boolean') {
        return value ? 'true' : 'false';
    }
    
    if (typeof value === 'number') {
        return value.toString();
    }
    
    if (value instanceof Date) {
        return `'${value.toISOString().slice(0, 19).replace('T', ' ')}'`;
    }
    
    return `'${value}'`;
};

// Main component to convert SQL to PHP Array
const SqlToPhpArray: React.FC<SqlToPhpArrayProps> = () => {
    // State for the SQL input
    const [sqlInput, setSqlInput] = useState<string>('');
    // State for the PHP array output
    const [phpArrayOutput, setPhpArrayOutput] = useState<string>('');
    // State for the PHP PDO code
    const [phpPdoCode, setPhpPdoCode] = useState<string>('');
    // State for the PHP Laravel code
    const [phpLaravelCode, setPhpLaravelCode] = useState<string>('');
    // State for the query results
    const [queryResults, setQueryResults] = useState<any[] | null>(null);
    // State for error messages
    const [error, setError] = useState<string>('');
    // State for loading indicator
    const [isLoading, setIsLoading] = useState<boolean>(false);
    // State for showing database schema
    const [showSchema, setShowSchema] = useState<boolean>(false);
    // State to track if the conversion happened
    const [isConverted, setIsConverted] = useState<boolean>(false);
    // State for copied indicator
    const [copied, setCopied] = useState<string | null>(null);
    // State for active tab
    const [activeTab, setActiveTab] = useState<string>('phpArray');
    
    // Use example query
    const useExampleQuery = () => {
        const exampleQuery = 'SELECT * FROM products WHERE category = "Electronics";';
        setSqlInput(exampleQuery);
        setError('');
        setIsConverted(false);
        setPhpArrayOutput('');
        setPhpPdoCode('');
        setPhpLaravelCode('');
        setQueryResults(null);
    };

    // Convert SQL to PHP Array
    const convertSqlToPhpArray = () => {
        setError('');
        setIsLoading(true);
        setIsConverted(false);
        setPhpArrayOutput('');
        setPhpPdoCode('');
        setPhpLaravelCode('');
        setQueryResults(null);

        if (!sqlInput.trim()) {
            setError('Please enter an SQL query');
            setIsLoading(false);
            return;
        }

        try {
            // Parse the SQL query and convert to PHP array
            const { phpQuery, results } = sqlToPhpArray(sqlInput, mockDatabase);

            // Format the PHP array syntax
            const formattedPhpArray = formatPhpArraySyntax(results, phpQuery.table);
            setPhpArrayOutput(formattedPhpArray);

            // Format PHP PDO query
            const formattedPhpPdoCode = formatPhpPdoQuery(phpQuery);
            setPhpPdoCode(formattedPhpPdoCode);

            // Format PHP Laravel query
            const formattedPhpLaravelCode = formatPhpLaravelQuery(phpQuery);
            setPhpLaravelCode(formattedPhpLaravelCode);

            // Set the query results
            setQueryResults(results);
            
            setIsConverted(true);
            setIsLoading(false);
        } catch (e) {
            setError(`Error converting SQL query: ${e instanceof Error ? e.message : String(e)}`);
            setIsLoading(false);
        }
    };

    // Clear all inputs and reset states
    const clearAll = () => {
        setSqlInput('');
        setPhpArrayOutput('');
        setPhpPdoCode('');
        setPhpLaravelCode('');
        setQueryResults(null);
        setError('');
        setIsConverted(false);
    };

    // Copy text to clipboard
    const copyToClipboard = (text: string, type: string) => {
        navigator.clipboard.writeText(text)
            .then(() => {
                setCopied(type);
                setTimeout(() => setCopied(null), 2000);
            })
            .catch(() => {
                setError('Failed to copy to clipboard');
            });
    };

    // Render database schema
    const renderDatabaseSchema = () => {
        return (
            <div className="bg-blue-50 p-4 rounded-md border border-blue-100 text-sm">
                <h3 className="font-medium mb-2">Database Schema</h3>
                {Object.values(mockDatabase).map(table => (
                    <div key={table.name} className="mb-3">
                        <div className="font-medium">{table.name}</div>
                        <div className="pl-4">
                            {table.records.length > 0 && (
                                <>
                                    <div className="text-xs font-medium mt-1">Sample Record Structure:</div>
                                    <pre className="text-xs bg-blue-100 p-2 rounded mt-1">
                                        {JSON.stringify(table.records[0], null, 2)}
                                    </pre>
                                </>
                            )}
                        </div>
                    </div>
                ))}
                <p className="mt-3 text-blue-800">
                    This is a mock database for demonstration. In a real application, you would
                    connect to an actual database.
                </p>
            </div>
        );
    };

    return (
        <div className="max-w-6xl mx-auto">
            <h1 className="text-2xl font-bold mb-6">SQL to PHP Array Converter</h1>

            {/* Description */}
            <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
                <p className="text-blue-800">
                    Convert SQL queries to PHP arrays and PDO/Laravel queries. This tool helps you transform your SQL knowledge to PHP's array format and database query implementations.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Input Section */}
                <div className="flex flex-col h-full">
                    <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
                        <div className="flex justify-between items-center mb-3">
                            <label htmlFor="sqlInput" className="font-medium text-gray-700">
                                SQL Query
                            </label>
                            <div className="flex space-x-2">
                                <button
                                    className="text-sm text-blue-600 hover:text-blue-800"
                                    onClick={useExampleQuery}
                                >
                                    Use Example
                                </button>
                                <button
                                    className="text-sm text-gray-600 hover:text-gray-800"
                                    onClick={() => setSqlInput('')}
                                >
                                    Clear
                                </button>
                            </div>
                        </div>

                        <textarea
                            id="sqlInput"
                            className="w-full h-64 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                            value={sqlInput}
                            onChange={(e) => {
                                setSqlInput(e.target.value);
                                setError('');
                                setIsConverted(false);
                            }}
                            placeholder="Enter your SQL query here, e.g.,
SELECT * FROM users WHERE created_at > '2023-01-01';"
                        />

                        <div className="mt-3">
                            <button
                                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                onClick={() => setShowSchema(!showSchema)}
                            >
                                <span>{showSchema ? 'Hide' : 'Show'} Database Schema</span>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={`h-4 w-4 ml-1 transition-transform ${showSchema ? 'transform rotate-180' : ''}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                        </div>

                        {showSchema && (
                            <div className="mt-3">
                                {renderDatabaseSchema()}
                            </div>
                        )}
                    </div>

                    <div className="mt-4 flex justify-between">
                        <button
                            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                            onClick={convertSqlToPhpArray}
                            disabled={isLoading}
                        >
                            {isLoading ? (
                                <div className="flex items-center">
                                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                    </svg>
                                    Converting...
                                </div>
                            ) : (
                                'Convert to PHP'
                            )}
                        </button>

                        <button
                            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                            onClick={clearAll}
                        >
                            Clear All
                        </button>
                    </div>
                </div>

                {/* Output Section - Tabs for different outputs */}
                <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
                    {error && (
                        <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                            <p className="font-medium">Error</p>
                            <p>{error}</p>
                        </div>
                    )}

                    {!error && !isConverted && (
                        <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                            <div className="text-center">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                </svg>
                                <p>
                                    PHP code will appear here after converting the SQL query.
                                </p>
                            </div>
                        </div>
                    )}

                    {!error && isConverted && (
                        <div className="flex-grow flex flex-col">
                            {/* Tabs */}
                            <div className="border-b border-gray-200">
                                <nav className="-mb-px flex" aria-label="Tabs">
                                    <button
                                        className={`border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 w-1/4 py-2 px-1 text-center border-b-2 font-medium text-sm ${activeTab === 'phpArray' ? 'border-blue-500 text-blue-600' : ''}`}
                                        onClick={() => setActiveTab('phpArray')}
                                    >
                                        PHP Array
                                    </button>
                                    <button
                                        className={`border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 w-1/4 py-2 px-1 text-center border-b-2 font-medium text-sm ${activeTab === 'phpPdo' ? 'border-blue-500 text-blue-600' : ''}`}
                                        onClick={() => setActiveTab('phpPdo')}
                                    >
                                        PHP PDO
                                    </button>
                                    <button
                                        className={`border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 w-1/4 py-2 px-1 text-center border-b-2 font-medium text-sm ${activeTab === 'phpLaravel' ? 'border-blue-500 text-blue-600' : ''}`}
                                        onClick={() => setActiveTab('phpLaravel')}
                                    >
                                        Laravel
                                    </button>
                                    <button
                                        className={`border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 w-1/4 py-2 px-1 text-center border-b-2 font-medium text-sm ${activeTab === 'results' ? 'border-blue-500 text-blue-600' : ''}`}
                                        onClick={() => setActiveTab('results')}
                                    >
                                        Results
                                    </button>
                                </nav>
                            </div>

                            {/* PHP Array Output */}
                            {activeTab === 'phpArray' && (
                                <div className="mt-4">
                                    <div className="flex justify-between items-center mb-2">
                                        <h3 className="font-medium">PHP Array</h3>
                                        <button
                                            className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                            onClick={() => copyToClipboard(phpArrayOutput, 'phpArray')}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-4 w-4 mr-1"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                            </svg>
                                            {copied === 'phpArray' ? 'Copied!' : 'Copy'}
                                        </button>
                                    </div>
                                    <pre className="bg-gray-50 p-3 rounded-md border border-gray-200 overflow-auto text-sm font-mono h-64">
                                        {phpArrayOutput}
                                    </pre>
                                </div>
                            )}

                            {/* PHP PDO Output */}
                            {activeTab === 'phpPdo' && (
                                <div className="mt-4">
                                    <div className="flex justify-between items-center mb-2">
                                        <h3 className="font-medium">PHP PDO Query</h3>
                                        <button
                                            className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                            onClick={() => copyToClipboard(phpPdoCode, 'phpPdo')}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-4 w-4 mr-1"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                            </svg>
                                            {copied === 'phpPdo' ? 'Copied!' : 'Copy'}
                                        </button>
                                    </div>
                                    <pre className="bg-gray-50 p-3 rounded-md border border-gray-200 overflow-auto text-sm font-mono h-64">
                                        {phpPdoCode}
                                    </pre>
                                </div>
                            )}

                            {/* PHP Laravel Output */}
                            {activeTab === 'phpLaravel' && (
                                <div className="mt-4">
                                    <div className="flex justify-between items-center mb-2">
                                        <h3 className="font-medium">Laravel Query Builder</h3>
                                        <button
                                            className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                            onClick={() => copyToClipboard(phpLaravelCode, 'phpLaravel')}
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                className="h-4 w-4 mr-1"
                                                fill="none"
                                                viewBox="0 0 24 24"
                                                stroke="currentColor"
                                            >
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                            </svg>
                                            {copied === 'phpLaravel' ? 'Copied!' : 'Copy'}
                                        </button>
                                    </div>
                                    <pre className="bg-gray-50 p-3 rounded-md border border-gray-200 overflow-auto text-sm font-mono h-64">
                                        {phpLaravelCode}
                                    </pre>
                                </div>
                            )}

                            {/* Query Results */}
                            {activeTab === 'results' && (
                                <div className="mt-4">
                                    <div className="flex justify-between items-center mb-2">
                                        <h3 className="font-medium">Query Results (Preview)</h3>
                                        <span className="text-sm text-gray-500">
                                            {queryResults?.length} record(s) found
                                        </span>
                                    </div>
                                    <div className="bg-gray-50 p-3 rounded-md border border-gray-200 overflow-auto h-64">
                                        {queryResults && queryResults.length > 0 ? (
                                            <pre className="text-sm font-mono">
                                                {JSON.stringify(queryResults, null, 2)}
                                            </pre>
                                        ) : (
                                            <div className="text-gray-500 text-center py-4">
                                                No results found
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {isConverted && (
                        <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
                            <div className="flex items-center mb-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <p className="font-medium">SQL Query Successfully Converted to PHP!</p>
                            </div>
                            <p className="text-sm">
                                You can copy the PHP array syntax or use the generated PHP PDO/Laravel code in your application.
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Information Section */}
            <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
                <h2 className="text-lg font-semibold mb-4">SQL to PHP Translation Guide</h2>

                <div className="space-y-4">
                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">Understanding the Conversion</h3>
                        <p className="text-gray-600">
                            This tool helps you convert SQL queries to PHP array syntax and database query code. It translates SELECT statements into PHP array structures and generates code for PHP PDO and Laravel's Query Builder.
                        </p>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">SQL to PHP Mapping</h3>
                        <div className="overflow-x-auto">
                            <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                    <tr>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">SQL Concept</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">PHP Equivalent</th>
                                        <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Example</th>
                                    </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200 text-sm">
                                    <tr>
                                        <td className="px-6 py-4 whitespace-nowrap">Table</td>
                                        <td className="px-6 py-4 whitespace-nowrap">Array Variable</td>
                                        <td className="px-6 py-4">users table → $users array</td>
                                    </tr>
                                    <tr>
                                        <td className="px-6 py-4 whitespace-nowrap">Row</td>
                                        <td className="px-6 py-4 whitespace-nowrap">Associative Array</td>
                                        <td className="px-6 py-4">Row in users → Array element in $users</td>
                                    </tr>
                                    <tr>
                                        <td className="px-6 py-4 whitespace-nowrap">Column</td>
                                        <td className="px-6 py-4 whitespace-nowrap">Array Key</td>
                                        <td className="px-6 py-4">name column → 'name' key in array</td>
                                    </tr>
                                    <tr>
                                        <td className="px-6 py-4 whitespace-nowrap">LIMIT</td>
                                        <td className="px-6 py-4 whitespace-nowrap">array_slice()</td>
                                        <td className="px-6 py-4">LIMIT 10 → array_slice($array, 0, 10)</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">Features</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li><strong>SQL Query Parsing</strong> - Parse basic SQL SELECT statements</li>
                            <li><strong>PHP Array Generation</strong> - Convert to PHP's array syntax</li>
                            <li><strong>PDO Code Generation</strong> - Create ready-to-use PHP PDO database code</li>
                            <li><strong>Laravel Query Builder</strong> - Generate Laravel-style query builder code</li>
                            <li><strong>Results Preview</strong> - View the query results against mock data</li>
                            <li><strong>Multiple Output Formats</strong> - See both raw PHP array syntax and application code</li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">Limitations of This Demo</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li>Uses a mock in-memory database with predefined records</li>
                            <li>Only supports basic SELECT queries with simple WHERE clauses</li>
                            <li>Limited SQL syntax support (no JOINs, GROUP BY, HAVING, etc.)</li>
                            <li>Complex PHP features like prepared statements are simplified</li>
                            <li>Doesn't support complex data types or nested structures</li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">Supported SQL Features</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li><strong>SELECT columns</strong> - Translated to PHP array keys</li>
                            <li><strong>FROM table</strong> - Translated to PHP array variable</li>
                            <li><strong>WHERE condition</strong> - Translated to PHP filtering logic</li>
                            <li><strong>ORDER BY column</strong> - Translated to PHP sorting</li>
                            <li><strong>LIMIT number</strong> - Translated to PHP array slicing</li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">PHP Database Resources</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li><a href="https://www.php.net/manual/en/book.pdo.php" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">PHP PDO Documentation</a></li>
                            <li><a href="https://laravel.com/docs/database" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Laravel Query Builder Documentation</a></li>
                            <li><a href="https://www.php.net/manual/en/language.types.array.php" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">PHP Arrays Documentation</a></li>
                            <li><a href="https://www.php.net/manual/en/ref.array.php" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">PHP Array Functions</a></li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SqlToPhpArray;